import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

interface PartsFilterProps {
  onFilterChange: (filters: {
    brands: string[];
    minPrice?: number;
    maxPrice?: number;
    deliveryDate?: string;
  }) => void;
  brands: string[];
}

export const PartsFilter: React.FC<PartsFilterProps> = ({ onFilterChange, brands }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState({
    min: '',
    max: ''
  });
  const [deliveryDate, setDeliveryDate] = useState<string>('');

  const handleBrandChange = (brand: string) => {
    setSelectedBrands(prev => {
      const newBrands = prev.includes(brand)
        ? prev.filter(b => b !== brand)
        : [...prev, brand];
      
      onFilterChange({
        brands: newBrands,
        minPrice: priceRange.min ? Number(priceRange.min) : undefined,
        maxPrice: priceRange.max ? Number(priceRange.max) : undefined,
        deliveryDate
      });
      
      return newBrands;
    });
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPriceRange(prev => {
      const newRange = { ...prev, [name]: value };
      
      onFilterChange({
        brands: selectedBrands,
        minPrice: newRange.min ? Number(newRange.min) : undefined,
        maxPrice: newRange.max ? Number(newRange.max) : undefined,
        deliveryDate
      });
      
      return newRange;
    });
  };

  const handleDeliveryDateChange = (date: string) => {
    setDeliveryDate(date);
    onFilterChange({
      brands: selectedBrands,
      minPrice: priceRange.min ? Number(priceRange.min) : undefined,
      maxPrice: priceRange.max ? Number(priceRange.max) : undefined,
      deliveryDate: date
    });
  };

  const clearFilters = () => {
    setSelectedBrands([]);
    setPriceRange({ min: '', max: '' });
    setDeliveryDate('');
    onFilterChange({ brands: [] });
  };

  return (
    <>
      {/* Мобильная кнопка фильтра */}
      <button
        onClick={() => setIsOpen(true)}
        className="md:hidden w-full px-4 py-2 rounded-lg shadow-lg shadow"
      >
        Фильтры
      </button>

      {/* Мобильное меню фильтров */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-50 md:hidden ${
          isOpen ? 'block' : 'hidden'
        }`}
        onClick={() => setIsOpen(false)}
      >
        <div
          className="absolute right-0 top-0 h-full w-80 bg-white p-4 shadow-lg overflow-y-auto"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex justify-between items-center mb-4 sticky top-0 bg-white py-2">
            <h3 className="text-lg font-semibold">Фильтры</h3>
            <button onClick={() => setIsOpen(false)}>
              <X className="w-5 h-5" />
            </button>
          </div>
          <FilterContent
            brands={brands}
            selectedBrands={selectedBrands}
            priceRange={priceRange}
            deliveryDate={deliveryDate}
            onBrandChange={handleBrandChange}
            onPriceChange={handlePriceChange}
            onDeliveryDateChange={handleDeliveryDateChange}
            onClear={clearFilters}
          />
        </div>
      </div>

      {/* Десктопный сайдбар */}
      <div className="hidden md:block w-64 shrink-0">
        <div className="sticky top-20 pl-4 pr-4 pt-6 mt-14 rounded-lg">
          <FilterContent
            brands={brands}
            selectedBrands={selectedBrands}
            priceRange={priceRange}
            deliveryDate={deliveryDate}
            onBrandChange={handleBrandChange}
            onPriceChange={handlePriceChange}
            onDeliveryDateChange={handleDeliveryDateChange}
            onClear={clearFilters}
          />
        </div>
      </div>
    </>
  );
};

interface FilterContentProps {
  brands: string[];
  selectedBrands: string[];
  priceRange: { min: string; max: string };
  deliveryDate: string;
  onBrandChange: (brand: string) => void;
  onPriceChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDeliveryDateChange: (date: string) => void;
  onClear: () => void;
}

const FilterContent: React.FC<FilterContentProps> = ({
  brands,
  selectedBrands,
  priceRange,
  deliveryDate,
  onBrandChange,
  onPriceChange,
  onDeliveryDateChange,
  onClear
}) => {
  return (
    <div className="space-y-6">
      <div>
        <h4 className="font-medium mb-4">Производитель</h4>
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {brands.map(brand => (
            <label key={brand} className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={selectedBrands.includes(brand)}
                onChange={() => onBrandChange(brand)}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="text-sm">{brand}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <h4 className="font-medium mb-2">Дата доставки</h4>
        <div className="space-y-2">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={deliveryDate === 'today'}
              onChange={(e) => onDeliveryDateChange(e.target.checked ? 'today' : '')}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm">Сегодня</span>
          </label>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={deliveryDate === 'tomorrow'}
              onChange={(e) => onDeliveryDateChange(e.target.checked ? 'tomorrow' : '')}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm">Завтра</span>
          </label>
        </div>
      </div>

      <div>
        <h4 className="font-medium mb-2">Цена</h4>
        <div className="flex space-x-2">
          <input
            type="number"
            name="min"
            value={priceRange.min}
            onChange={onPriceChange}
            placeholder="От"
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary pl-2"
          />
          <input
            type="number"
            name="max"
            value={priceRange.max}
            onChange={onPriceChange}
            placeholder="До"
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary pl-2"
          />
        </div>
      </div>

      <button
        onClick={onClear}
        className="w-full py-2 text-sm text-gray-600 hover:text-gray-800"
      >
        Сбросить фильтры
      </button>
    </div>
  );
}; 